import type React from "react";
import { Drawer, Group, Stack, Text } from "@mantine/core";
import { Alert, Button } from "@xplatform/design-system";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import UploadDropzone from "@components/UploadDropzone/UploadDropzone";
import type {
  FailedUploadState,
  UploadProgressState,
} from "@hooks/useJobSubmit";
import UploadProgressBar from "@components/UploadProgressBar/UploadProgressBar";

interface UploadDrawerProps {
  opened: boolean;
  onClose: () => void;
  busy: boolean;
  uploadProgress: UploadProgressState | null;
  onFilesSelected: (files: File[]) => void;
  failedUpload: FailedUploadState | null;
  onRetry: () => void;
  onDismissFailed: () => void;
}

const UploadDrawer: React.FC<UploadDrawerProps> = ({
  opened,
  onClose,
  busy,
  uploadProgress,
  onFilesSelected,
  failedUpload,
  onRetry,
  onDismissFailed,
}) => {
  const { t } = useTranslation();
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      title={
        <Stack gap={0}>
          <Text fw={600}>{t("mappingServicePage.upload.title")}</Text>
          <Text size="xs" c="dimmed">
            {t("mappingServicePage.upload.hint")}
          </Text>
        </Stack>
      }
    >
      <Stack gap="sm">
        {failedUpload && !busy && (
          <Alert
            color="red"
            icon={<IconAlertTriangle size={18} />}
            title={t("mappingServicePage.upload.retryTitle", {
              filename: failedUpload.filename,
            })}
            withCloseButton
            onClose={onDismissFailed}
          >
            <Text size="sm" mb="xs">
              {t("mappingServicePage.upload.retryHint")}
            </Text>
            <Group gap="xs">
              <Button
                size="xs"
                leftSection={<IconRefresh size={14} />}
                onClick={onRetry}
              >
                {t("mappingServicePage.upload.retry")}
              </Button>
              <Button size="xs" variant="subtle" onClick={onDismissFailed}>
                {t("mappingServicePage.upload.dismiss")}
              </Button>
            </Group>
          </Alert>
        )}
        <UploadDropzone
          accept=".json,.csv,.xlsx,.txt,.zip"
          busy={busy}
          multiple
          onFilesSelected={onFilesSelected}
        />
        {uploadProgress && <UploadProgressBar uploadProgress={uploadProgress} />}
      </Stack>
    </Drawer>
  );
};

export default UploadDrawer;
